/**
 * Energy Certificate Simulator - LocalStorage Hook
 *
 * Custom hook for persisting simulator state in localStorage
 * Provides automatic save/load with error handling
 */

import { useState, useCallback, useMemo } from 'react';
import type { SimulatorInput, SavedSimulation } from '../types';
import { STORAGE_KEYS } from '../types';

// =============================================================================
// GENERIC LOCALSTORAGE HOOK
// =============================================================================

/**
 * Generic hook for localStorage state management
 * @param key - localStorage key
 * @param initialValue - default value if nothing stored
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T
): [T, (value: T | ((prev: T) => T)) => void, () => void] {
  // Get stored value or use initial
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return initialValue;
    }

    try {
      const item = window.localStorage.getItem(key);
      return item ? (JSON.parse(item) as T) : initialValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  // Update localStorage when value changes
  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      try {
        const valueToStore = value instanceof Function ? value(storedValue) : value;
        setStoredValue(valueToStore);

        if (typeof window !== 'undefined') {
          window.localStorage.setItem(key, JSON.stringify(valueToStore));
        }
      } catch (error) {
        console.warn(`Error setting localStorage key "${key}":`, error);
      }
    },
    [key, storedValue]
  );

  // Clear stored value
  const clearValue = useCallback(() => {
    try {
      setStoredValue(initialValue);
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(key);
      }
    } catch (error) {
      console.warn(`Error clearing localStorage key "${key}":`, error);
    }
  }, [key, initialValue]);

  return [storedValue, setValue, clearValue];
}

// =============================================================================
// SIMULATOR FORM STATE HOOK
// =============================================================================

/**
 * Default simulator input values
 */
export const DEFAULT_SIMULATOR_INPUT: SimulatorInput = {
  usableArea: 120,
  buildingPeriod: '1999_2008',
  buildingType: 'detached',
  floors: 2,
  occupants: 2,
  climateZone: 'III',
  apartmentFloorPosition: undefined,
  apartmentOrientation: undefined,
  renovationStatus: 'none',
  wallInsulation: 'partial',
  roofInsulation: 'partial',
  windowType: 'double_old',
  heatingSystem: 'gas_traditional',
  systemAge: 'medium',
  hotWaterSource: 'combined',
  hasSecondaryHeating: false,
  hasPV: false,
  renewables: [],
};

/**
 * Hook for persisting simulator form state
 */
export function useSimulatorFormStorage() {
  const [formState, setFormState, clearFormState] = useLocalStorage<SimulatorInput>(
    STORAGE_KEYS.FORM_STATE,
    DEFAULT_SIMULATOR_INPUT
  );

  // Merge stored state with defaults to ensure all fields exist.
  // Handles localStorage data from older versions that may be missing newly added fields.
  const mergedFormState = useMemo<SimulatorInput>(
    () => ({ ...DEFAULT_SIMULATOR_INPUT, ...formState }),
    [formState]
  );

  return {
    formState: mergedFormState,
    setFormState,
    clearFormState,
    resetToDefaults: () => setFormState(DEFAULT_SIMULATOR_INPUT),
  };
}

// =============================================================================
// SAVED SIMULATIONS HOOK
// =============================================================================

/**
 * Hook for managing saved simulations
 */
export function useSavedSimulations() {
  const [simulations, setSimulations, clearAll] = useLocalStorage<SavedSimulation[]>(
    STORAGE_KEYS.SAVED_SIMULATIONS,
    []
  );

  /**
   * Save a new simulation
   */
  const saveSimulation = useCallback(
    (simulation: Omit<SavedSimulation, 'id' | 'createdAt' | 'updatedAt'>) => {
      const now = new Date().toISOString();
      const newSimulation: SavedSimulation = {
        ...simulation,
        id: `sim-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        createdAt: now,
        updatedAt: now,
      };

      setSimulations(prev => [...prev, newSimulation]);
      return newSimulation.id;
    },
    [setSimulations]
  );

  /**
   * Update an existing simulation
   */
  const updateSimulation = useCallback(
    (id: string, updates: Partial<Omit<SavedSimulation, 'id' | 'createdAt'>>) => {
      setSimulations(prev =>
        prev.map(sim =>
          sim.id === id
            ? { ...sim, ...updates, updatedAt: new Date().toISOString() }
            : sim
        )
      );
    },
    [setSimulations]
  );

  /**
   * Delete a simulation
   */
  const deleteSimulation = useCallback(
    (id: string) => {
      setSimulations(prev => prev.filter(sim => sim.id !== id));
    },
    [setSimulations]
  );

  /**
   * Get a simulation by ID
   */
  const getSimulation = useCallback(
    (id: string): SavedSimulation | undefined => {
      return simulations.find(sim => sim.id === id);
    },
    [simulations]
  );

  return {
    simulations,
    saveSimulation,
    updateSimulation,
    deleteSimulation,
    getSimulation,
    clearAll,
    count: simulations.length,
  };
}

// =============================================================================
// USER PREFERENCES HOOK
// =============================================================================

export interface UserPreferences {
  showAdvancedOptions: boolean;
  autoSave: boolean;
  language: 'pl';
}

export const DEFAULT_PREFERENCES: UserPreferences = {
  showAdvancedOptions: false,
  autoSave: true,
  language: 'pl',
};

/**
 * Hook for user preferences
 */
export function useSimulatorPreferences() {
  const [preferences, setPreferences] = useLocalStorage<UserPreferences>(
    STORAGE_KEYS.PREFERENCES,
    DEFAULT_PREFERENCES
  );

  const updatePreference = useCallback(
    <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => {
      setPreferences(prev => ({ ...prev, [key]: value }));
    },
    [setPreferences]
  );

  return {
    preferences,
    updatePreference,
    resetPreferences: () => setPreferences(DEFAULT_PREFERENCES),
  };
}
